import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Unstable_Grid2'; // TODO:  probably need to update this import in the future
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import Image from '../Image';
import Iconify from '../Iconify';
import OverflowTooltip from '../OverflowTooltip';
import { FormProvider, RHFTextField } from '../hook-form';
import { setUpdateEcommVariants } from '../../redux/thunks/ecommVariantThunks';
import { useDispatch } from '../../redux/store';
import { delay, determineModelTypeInfo } from '../../utils/levarHelpers';
import { fetchModel } from '../../utils/fetchData';
import { setUpdateModel } from '../../redux/thunks/modelThunks';
import { LEVAR_3D_MODEL_ID_TYPES } from '../../utils/levar_data';

// ----------------------------------------------------------------------

VariantOptionsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  dialogData: PropTypes.object.isRequired,
};

const VariantInfoItem = styled(Paper)(({ theme }) => ({
  backgroundColor: '#f9f9f9', // Light gray background
  // backgroundColor: '#f0f7ff', // Light blue background
  padding: theme.spacing(1.5),
  boxShadow: 'none',
}));

const OptionsFieldItem = styled(Paper)(({ theme }) => ({
  // backgroundColor: '#f0f7ff', // Light blue background
  backgroundColor: '#f9f9f9', // Light gray background
  padding: theme.spacing(1.5),
  boxShadow: 'none',
}));

export default function VariantOptionsDialog({ onClose, dialogData }) {
  // console.log('dialogData', dialogData)
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const OptionSubSchema = Yup.object().shape({
    option_name: Yup.string().nullable(),
    option_value: Yup.string().nullable(),
    option_prop: Yup.string().nullable(),
  });

  const VariantOptionsSchema = Yup.object().shape({
    current_variant_id: Yup.string(),
    option_1: OptionSubSchema,
    option_2: OptionSubSchema,
    option_3: OptionSubSchema,
  });

  const defaultValues = {
    current_variant_id: '',
    current_variant_title: '',
    current_variant_image_url: '',
    option_1: {
      option_name: '',
      option_value: '',
      option_prop: '',
    },
    option_2: {
      option_name: '',
      option_value: '',
      option_prop: '',
    },
    option_3: {
      option_name: '',
      option_value: '',
      option_prop: '',
    },
    viewerVariant: null, // null = no model, true = enabled, false = disabled
    currentModelId: null, // id of the model
    currentModelType: null, // type of the model (e.g. "model_3d")
    modelUrl: null, // url of the model
  };

  const methods = useForm({
    resolver: yupResolver(VariantOptionsSchema),
    defaultValues,
  });

  const {
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const [state, setState] = useState({
    current_index: 0,
    max_index: 0,
    variants: [],
    pageReady: false,
    isWriting: false,
    viewerVariant: null, // null = no model, true = enabled, false = disabled
    currentModelId: null,
    currentModelType: null,
    modelUrl: null,
  });

  useEffect(() => {
    let isComponentMounted = true;

    if(isComponentMounted && dialogData.variants && !state.pageReady) {
      const getResource = async () => {
        let initialIndex = 0;
        
        // IF A SINGLE VARIANT IS SELECTED, SET THE VALUES FOR THAT VARIANT
        if(dialogData.selectedVariant) {
          const selectedVariantIndex = dialogData.variants.findIndex(variant => variant.variant_id === dialogData.selectedVariant);
          const selectedVariant = dialogData.variants[selectedVariantIndex];
          
          if(selectedVariant && selectedVariantIndex !== -1) {
            initialIndex = selectedVariantIndex;
            // console.log('selectedVariant', selectedVariant)
            const variantModelType = Object.keys(LEVAR_3D_MODEL_ID_TYPES).find(key => selectedVariant[key]);
            // console.log('variantModelType', variantModelType)
            
            // Only fetch model info if variant has a valid model type and model ID
            if(variantModelType && selectedVariant[variantModelType]) {
              const { modelInfo, modelUrl } = await fetchVariantModelInfo(selectedVariant[variantModelType]);
              // console.log('modelInfo', modelInfo)
              
              // Extract viewer_variant_switch from the model info
              const viewerVariantSwitch = modelInfo?.viewer_variant_switch || false;
              
              // console.log('viewer_variant_switch:', viewerVariantSwitch)
              
              setState(prevState => ({
                ...prevState,
                current_index: initialIndex,
                max_index: dialogData.variants.length - 1,
                variants: dialogData.variants,
                viewerVariant: viewerVariantSwitch,
                currentModelId: selectedVariant[variantModelType],
                currentModelType: variantModelType,
                modelUrl,
                current_variant_image_url: selectedVariant.variant_image_url,
                pageReady: true,
                isWriting: false,
              }));
            } else {
              // console.log('No 3D model connected to this variant')
              setState(prevState => ({
                ...prevState,
                current_index: initialIndex,
                max_index: dialogData.variants.length - 1,
                variants: dialogData.variants,
                viewerVariant: null,
                currentModelId: null,
                currentModelType: null,
                modelUrl: null,
                current_variant_image_url: selectedVariant.variant_image_url,
                pageReady: true,
                isWriting: false,
              }));
            }

            setValue("current_variant_id", dialogData.selectedVariant);
            setValue("current_variant_title", selectedVariant.variant_title);
            if(selectedVariant.options && Array.isArray(selectedVariant.options)) {
              selectedVariant.options.forEach((option, index) => {
                if(option) {
                  setValue(`option_${index + 1}.option_name`, option?.option_name || '');
                  setValue(`option_${index + 1}.option_value`, option?.option_value || '');
                  setValue(`option_${index + 1}.option_prop`, option?.option_prop || '');
                }
              });
            }
          }
        } else { // IF NO VARIANT IS SELECTED, SET THE VALUES FOR THE CURRENT INDEX
          // console.log('*******no variant selected********')
          const currentVariant = dialogData.variants[initialIndex];
          if(currentVariant) {
            setValue("current_variant_id", currentVariant.variant_id);
            setValue("current_variant_title", currentVariant.variant_title);
            if(currentVariant.options && Array.isArray(currentVariant.options)) {
              currentVariant.options.forEach((option, index) => {
                if(option) {
                  setValue(`option_${index + 1}.option_name`, option?.option_name || '');
                  setValue(`option_${index + 1}.option_value`, option?.option_value || '');
                  setValue(`option_${index + 1}.option_prop`, option?.option_prop || '');
                }
              });
            }
          }

          setState((prevState) => ({
            ...prevState,
            current_index: initialIndex,
            max_index: dialogData.variants.length - 1,
            variants: dialogData.variants,
            pageReady: true,
            isWriting: false,
          }))
        }
      };
      getResource();
    }
    return function cleanup() {
        isComponentMounted = false;
    }
  }, [dialogData, dispatch, setValue])

  const fetchVariantModelInfo = async (modelID) => {
    const variantModelInfo = await fetchModel(modelID);
    // console.log('variantModelInfo', variantModelInfo)
    const modelTypeInfo = determineModelTypeInfo(variantModelInfo.search_response.model_type);
    // console.log('modelTypeInfo', modelTypeInfo)
    return {
      modelUrl: modelTypeInfo?.url || null,
      modelInfo: variantModelInfo?.search_response || null,
    };
  }

  const updateViewerVariantSwitch = async (newViewerVariantSwitch) => {
    if (!state.currentModelId || !state.currentModelType) {
      console.error('No model selected to update');
      return;
    }

    setState(prevState => ({
      ...prevState,
      isWriting: true,
    }));

    try {
      await dispatch(setUpdateModel({ model3dId: state.currentModelId, fields: { viewer_variant_switch: newViewerVariantSwitch } }));
      
      setState(prevState => ({
        ...prevState,
        viewerVariant: newViewerVariantSwitch,
        isWriting: false,
      }));

      enqueueSnackbar(`Viewer variant switch ${newViewerVariantSwitch ? 'enabled' : 'disabled'} for this model`, { 
        variant: 'success' 
      });
    } catch (error) {
      console.error('Error updating viewer variant switch:', error);
      setState(prevState => ({
        ...prevState,
        isWriting: false,
      }));
      enqueueSnackbar('Error updating viewer variant switch', { variant: 'error' });
    }
  };

  const navigateToVariant = async (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= state.variants.length) return;
    
    const targetVariant = state.variants[targetIndex];
    if (!targetVariant) return;

    // Update the current index
    setState(prevState => ({
      ...prevState,
      current_index: targetIndex,
    }));

    // Set form values for the new variant
    setValue("current_variant_id", targetVariant.variant_id);
    setValue("current_variant_title", targetVariant.variant_title);
    
    // Clear existing option values
    for (let i = 1; i <= 3; i += 1) {
      setValue(`option_${i}.option_name`, '');
      setValue(`option_${i}.option_value`, '');
      setValue(`option_${i}.option_prop`, '');
    }

    // Set option values if they exist
    if (targetVariant.options && Array.isArray(targetVariant.options)) {
      targetVariant.options.forEach((option, index) => {
        if (option) {
          setValue(`option_${index + 1}.option_name`, option?.option_name || '');
          setValue(`option_${index + 1}.option_value`, option?.option_value || '');
          setValue(`option_${index + 1}.option_prop`, option?.option_prop || '');
        }
      });
    }

    // Check if variant has a model and update viewer state
    const variantModelType = Object.keys(LEVAR_3D_MODEL_ID_TYPES).find(key => targetVariant[key]);
    
    if (variantModelType && targetVariant[variantModelType]) {
      const { modelInfo, modelUrl } = await fetchVariantModelInfo(targetVariant[variantModelType]);
      const viewerVariantSwitch = modelInfo?.viewer_variant_switch || false;
      
      setState(prevState => ({
        ...prevState,
        viewerVariant: viewerVariantSwitch,
        currentModelId: targetVariant[variantModelType],
        currentModelType: variantModelType,
        modelUrl,
        current_variant_image_url: targetVariant.variant_image_url,
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        viewerVariant: null,
        currentModelId: null,
        currentModelType: null,
        modelUrl: null,
        current_variant_image_url: targetVariant.variant_image_url,
      }));
    }
  };

  const handlePreviousVariant = () => {
    const prevIndex = state.current_index - 1;
    if (prevIndex >= 0) {
      navigateToVariant(prevIndex);
    }
  };

  const handleNextVariant = () => {
    const nextIndex = state.current_index + 1;
    if (nextIndex < state.variants.length) {
      navigateToVariant(nextIndex);
    }
  };

  const onSubmit = async () => {
    try {
      const options = []
      Object.entries(values).forEach(([key, value]) => {
        if (key.startsWith('option_')) {
          if(value.option_name !== '') {
            options.push(value);
          }
        }
      });


      await dispatch(setUpdateEcommVariants({ ecommVariantIds: values.current_variant_id, settingsToWrite: { options }, destructure: true }));
      enqueueSnackbar("Update Success!", { variant: 'success' });
    } catch (error) {
      console.error("onSubmit error", error);
    } finally {
      // Just reset the writing state but keep the form values and modal open
      setState((prevState) => ({
        ...prevState,
        isWriting: false,
      }));
    }
  };

  return (
    <Box >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      
        <Stack spacing={2} direction="column">
          
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Variant Options Builder
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Iconify icon={'ic:round-close'} width="20" height="20" />
            </IconButton>
          </Stack>

          <Divider />

          {/* Body */}
          <Box sx={{ mt: 1, px: 1 }}>
            <Grid container spacing={2}>
              
              {/* Variant Info / Change Model Viewer Variant Switch */}
              <Grid xs={12}>
                <VariantInfoItem>
                  <Stack spacing={1.5}>
                    {/* Variant Navigation Header */}
                    {state.variants.length > 1 && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Product Variant
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                            {state.current_index + 1} of {state.variants.length}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton 
                              onClick={handlePreviousVariant}
                              disabled={state.current_index === 0}
                              size="small"
                              sx={{ 
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'action.selected' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                width: 28,
                                height: 28
                              }}
                            >
                              <Iconify icon="mdi:chevron-left" width="16" height="16" />
                            </IconButton>
                            <IconButton 
                              onClick={handleNextVariant}
                              disabled={state.current_index === state.variants.length - 1}
                              size="small"
                              sx={{ 
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'action.selected' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                width: 28,
                                height: 28
                              }}
                            >
                              <Iconify icon="mdi:chevron-right" width="16" height="16" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    )}

                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Image
                          disabledEffect
                          alt={values.current_variant_id}
                          src={state.current_variant_image_url || ""}
                          sx={{ borderRadius: 1, width: 40, height: 40 }}
                        />
                        <OverflowTooltip 
                          value={values.current_variant_title} 
                          secondValue={values.current_variant_id} 
                          sx={{ textAlign: 'left', fontWeight: '600', fontSize: 14 }} 
                        />
                      </Stack>

                      {state.modelUrl && (
                        <Tooltip title={!state.viewerVariant ? "Enable variant switching to preview" : "Preview model with variant switching"}>
                          <span>
                            <Button
                              variant="outlined"
                              disabled={!state.viewerVariant}
                              size="small"
                              href={`${state.modelUrl}/?ecomm=${values.current_variant_id}`}
                              target="_blank"
                              rel="noopener"
                              startIcon={<Iconify icon="mdi:eye" />}
                              endIcon={<Iconify icon="mdi:open-in-new" />}
                              sx={{ 
                                minWidth: 100,
                                ...(state.viewerVariant && {
                                  color: 'primary.main',
                                  borderColor: 'primary.main'
                                })
                              }}
                            >
                              Preview
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>

                    <Divider />

                    {/* Variant Switching */}
                    {state.viewerVariant !== null ? (
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack sx={{ flex: 1, pr: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14 }}>
                              Variant Switching in 3D Viewer
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Allow customers to switch between product variants directly in the 3D viewer
                            </Typography>
                          </Stack>
                          
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              {state.viewerVariant ? "On" : "Off"}
                            </Typography>
                            <Switch 
                              checked={state.viewerVariant} 
                              onChange={(e) => updateViewerVariantSwitch(e.target.checked)}
                              disabled={state.isWriting}
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="mdi:alert-circle" sx={{ width: 16, height: 16, color: 'warning.main' }} />
                        <Typography variant="body2" color="text.secondary">
                          No 3D model connected - options editing disabled (Connect a 3D model to enable)
                        </Typography>
                      </Stack>
                    )}

                  </Stack>
                </VariantInfoItem>
              </Grid>
          
              {/* Options Helpers */}
              <Grid xs={12}>
                <Box sx={{ 
                  bgcolor: 'primary.lighter', 
                  borderRadius: 2,
                  p: 2,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box sx={{ 
                      bgcolor: 'primary.main', 
                      borderRadius: '50%', 
                      width: 24, 
                      height: 24, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Iconify icon="mdi:lightbulb" sx={{ width: 14, height: 14, color: 'white' }} />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                      How to Set Up Variant Options
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, fontStyle: 'italic' }}>
                      These display in the 3D viewer for customers
                    </Typography>
                  </Stack>
                  
                  <Box sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Grid container spacing={2}>
                      <Grid xs={4}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontSize: 12 }}>
                            NAME
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
                            Category (Color, Size, etc.)
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid xs={4}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontSize: 12 }}>
                            VALUE
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
                            Option text (Red, Large, etc.)
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid xs={4}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontSize: 12 }}>
                            PROPERTY
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
                            #hex | text | https:// | dropdown
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Options 1 */}
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_1.option_name" 
                    size="small" 
                    label="Option 1 Name"
                    placeholder="e.g., Color, Size, Material"
                  />
                </OptionsFieldItem>
              </Grid>
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_1.option_value" 
                    size="small" 
                    label="Option 1 Value"
                    placeholder="e.g., Red, Large, Cotton"
                  />
                </OptionsFieldItem>
              </Grid>
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_1.option_prop" 
                    size="small" 
                    label="Option 1 Prop"
                    placeholder="e.g., #FF0000, text, https://..."
                  />
                </OptionsFieldItem>
              </Grid>

              {/* Option 2 */}
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_2.option_name" 
                    size="small" 
                    label="Option 2 Name"
                    placeholder="e.g., Color, Size, Material"
                  />
                </OptionsFieldItem>
              </Grid>
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_2.option_value" 
                    size="small" 
                    label="Option 2 Value"
                    placeholder="e.g., Red, Large, Cotton"
                  />
                </OptionsFieldItem>
              </Grid>
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_2.option_prop" 
                    size="small" 
                    label="Option 2 Prop"
                    placeholder="e.g., #FF0000, text, https://..."
                  />
                </OptionsFieldItem>
              </Grid>

              {/* Option 3 */}
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_3.option_name" 
                    size="small" 
                    label="Option 3 Name"
                    placeholder="e.g., Color, Size, Material"
                  />
                </OptionsFieldItem>
              </Grid>
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_3.option_value" 
                    size="small" 
                    label="Option 3 Value"
                    placeholder="e.g., Red, Large, Cotton"
                  />
                </OptionsFieldItem>
              </Grid>
              <Grid xs={4}>
                <OptionsFieldItem>
                  <RHFTextField 
                    name="option_3.option_prop" 
                    size="small" 
                    label="Option 3 Prop"
                    placeholder="e.g., #FF0000, text, https://..."
                  />
                </OptionsFieldItem>
              </Grid>

            </Grid>
          </Box>

          <Divider />

          {/* Actions Footer */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Clear all option fields">
                  <Button
                    variant="outlined" 
                    size="small" 
                    onClick={() => reset()}
                    disabled={!state.viewerVariant || isSubmitting}
                    startIcon={<Iconify icon="mdi:refresh" />}
                    sx={{ minWidth: 80 }}
                  >
                    Reset
                  </Button>
                </Tooltip>
                {state.isWriting && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Iconify icon="eos-icons:three-dots-loading" sx={{ width: 16, height: 16 }} />
                    Updating...
                  </Typography>
                )}
              </Stack>
              
              <LoadingButton 
                variant="contained" 
                size="small" 
                type="submit"
                loading={isSubmitting}
                disabled={!state.viewerVariant}
                startIcon={<Iconify icon="mdi:content-save" />}
                sx={{ minWidth: 120 }}
              >
                Save Options
              </LoadingButton>
            </Stack>
          </Box>

        </Stack>
      </FormProvider>
    </Box>
  )
};